/**
 * Template Definition Bundle format and utilities for exporting / importing full Templates.
 */

import type {
  LaunchLanguage,
  TemplateDefinition,
  TemplateLocalization,
  TemplateVisibility,
} from '@pius/contracts';
import { findDuplicateKeys } from '@pius/contracts';

import type { TemplateDetail } from './api';

export interface TemplateBundle {
  schema_version: 1;
  generator: 'Pius Selections';
  exported_at: string;
  template: {
    name: string;
    visibility: TemplateVisibility;
    primary_language: LaunchLanguage;
    definition: TemplateDefinition;
    localizations?: TemplateLocalization[] | undefined;
  };
}

export function createTemplateBundle(
  detail: TemplateDetail,
  localizations?: TemplateLocalization[],
): TemplateBundle {
  // Use latest published or draft definition
  const draft = detail.versions.find((v) => !v.is_published);
  const published = detail.versions.filter((v) => v.is_published);
  const latestPublished = published[published.length - 1];
  const definition = draft?.definition ??
    latestPublished?.definition ?? {
      primary_language: 'en',
      sections: [],
      roles: [],
    };

  const bundle: TemplateBundle = {
    schema_version: 1,
    generator: 'Pius Selections',
    exported_at: new Date().toISOString(),
    template: {
      name: detail.name,
      visibility: detail.visibility,
      primary_language: definition.primary_language || 'en',
      definition,
    },
  };

  if (localizations && localizations.length > 0) {
    bundle.template.localizations = localizations;
  }

  return bundle;
}

export interface ValidationResult {
  valid: boolean;
  bundle?: TemplateBundle | undefined;
  errors: string[];
}

export function validateTemplateBundle(input: unknown): ValidationResult {
  const errors: string[] = [];

  if (!input || typeof input !== 'object') {
    return { valid: false, errors: ['File does not contain a valid JSON object.'] };
  }

  const obj = input as Record<string, unknown>;

  // Check top-level or direct definition
  let bundle: TemplateBundle;

  if (obj.schema_version === 1 && obj.template && typeof obj.template === 'object') {
    bundle = obj as unknown as TemplateBundle;
  } else if (obj.sections && Array.isArray(obj.sections)) {
    // Direct raw TemplateDefinition provided
    bundle = {
      schema_version: 1,
      generator: 'Pius Selections',
      exported_at: new Date().toISOString(),
      template: {
        name: typeof obj.name === 'string' ? obj.name : 'Imported Template',
        visibility: 'private',
        primary_language: (obj.primary_language as LaunchLanguage) || 'en',
        definition: {
          primary_language: (obj.primary_language as LaunchLanguage) || 'en',
          sections: obj.sections as unknown as TemplateDefinition['sections'],
          roles: Array.isArray(obj.roles)
            ? (obj.roles as unknown as TemplateDefinition['roles'])
            : [],
          extraordinary_form: Boolean(obj.extraordinary_form),
        },
      },
    };
  } else {
    return {
      valid: false,
      errors: ['Unrecognized template format. Must be a .pius.json bundle or TemplateDefinition.'],
    };
  }

  const { template } = bundle;
  if (!template.name || typeof template.name !== 'string' || template.name.trim() === '') {
    errors.push('Template name is required.');
  }

  const def = template.definition;
  if (!def || typeof def !== 'object') {
    errors.push('Template definition is missing.');
    return { valid: false, errors };
  }

  if (!Array.isArray(def.sections)) {
    errors.push('Template definition must contain a sections array.');
  } else {
    for (let sIdx = 0; sIdx < def.sections.length; sIdx++) {
      const sec = def.sections[sIdx];
      if (!sec || !sec.key || typeof sec.key !== 'string') {
        errors.push(`Section #${sIdx + 1} is missing a key.`);
      }
      if (!sec || !Array.isArray(sec.items)) {
        errors.push(`Section "${sec?.key || sIdx + 1}" must contain an items array.`);
      } else {
        for (let iIdx = 0; iIdx < sec.items.length; iIdx++) {
          const item = sec.items[iIdx];
          if (!item || !item.key || typeof item.key !== 'string') {
            errors.push(`Item #${iIdx + 1} in section "${sec.key}" is missing a key.`);
          }
          if (!item || !item.item_type || typeof item.item_type !== 'string') {
            errors.push(`Item "${item?.key ?? iIdx + 1}" is missing an item_type.`);
          }
        }
      }
    }
  }

  // Check duplicate keys
  const duplicateKeys = findDuplicateKeys(def);
  if (duplicateKeys.length > 0) {
    errors.push(`Duplicate keys found: ${duplicateKeys.join(', ')}`);
  }

  return {
    valid: errors.length === 0,
    bundle: errors.length === 0 ? bundle : undefined,
    errors,
  };
}

/** Trigger browser file download */
export function downloadFile(filename: string, content: string, mimeType: string): void {
  const blob = new Blob([content], { type: mimeType });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
